import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate } from "@/lib/format";
import { attT } from "@/lib/attendanceI18n";

export default function LeavePeriodsTab({ canEdit }) {
  const { theme, lang } = useI18n();
  const [employees, setEmployees] = useState([]);
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ user_id: "", from_date: "", to_date: "", type: "approved_leave", reason: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const [emp, lv] = await Promise.all([
        api.get("/admin/attendance/employees"),
        api.get("/admin/attendance/leave-periods"),
      ]);
      setEmployees(emp.data.items || []);
      setItems(lv.data.items || []);
    } catch (e) { setErr(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/admin/attendance/leave-periods", form);
      setForm({ user_id: "", from_date: "", to_date: "", type: "approved_leave", reason: "" });
      setAdding(false);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm(attT(lang, "confirm_delete"))) return;
    try {
      await api.delete(`/admin/attendance/leave-periods/${id}`);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const css = makeStyles(theme);

  return (
    <div data-testid="attendance-leaves-tab">
      {err && <div style={css.err}>{err}</div>}

      <div style={css.toolbar}>
        <div style={css.count}>{attT(lang, "leave.count")}: <strong>{items.length}</strong></div>
        {canEdit && !adding && (
          <button onClick={() => setAdding(true)} style={css.btnPrimary} data-testid="leave-add-btn">{attT(lang, "leave.add_btn")}</button>
        )}
      </div>

      {adding && (
        <div style={css.form} data-testid="leave-add-form">
          <div style={css.grid}>
            <Field label={attT(lang, "leave.form.employee")} theme={theme}>
              <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} style={css.input} data-testid="leave-user">
                <option value="">{attT(lang, "leave.form.select_ph")}</option>
                {employees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
            <Field label={attT(lang, "leave.form.from")} theme={theme}><input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} style={css.input} data-testid="leave-from" /></Field>
            <Field label={attT(lang, "leave.form.to")} theme={theme}><input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} style={css.input} data-testid="leave-to" /></Field>
            <Field label={attT(lang, "leave.form.type")} theme={theme}>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} style={css.input} data-testid="leave-type">
                {["approved_leave", "excused", "sick", "permitted"].map((k) => (
                  <option key={k} value={k}>{attT(lang, `leave.type.${k}`)}</option>
                ))}
              </select>
            </Field>
            <Field label={attT(lang, "leave.form.reason")} theme={theme}><input type="text" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} style={css.input} data-testid="leave-reason" placeholder={attT(lang, "leave.form.reason_ph")} /></Field>
          </div>
          <div style={css.formActions}>
            <button onClick={() => { setAdding(false); setForm({ user_id: "", from_date: "", to_date: "", type: "approved_leave", reason: "" }); }} style={css.btn}>{attT(lang, "cancel")}</button>
            <button onClick={submit} disabled={busy || !form.user_id || !form.from_date || !form.to_date} style={css.btnPrimary} data-testid="leave-submit">
              {busy ? attT(lang, "saving") : attT(lang, "leave.form.submit")}
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div style={css.empty}>{attT(lang, "leave.empty")}</div>
      ) : (
        <div style={css.list}>
          {items.map((it) => (
            <div key={it._id} style={css.row} data-testid={`leave-row-${it._id}`}>
              <div style={{ flex: 1 }}>
                <div style={css.rowName}>{it.full_name || it.username}</div>
                <div style={css.rowMeta}>
                  {attT(lang, `leave.type.${it.type}`)}{" — "}
                  {attT(lang, "leave.range", { from: fmtDate(it.from_date), to: fmtDate(it.to_date) })}
                </div>
                {it.reason && <div style={css.rowReason}>{attT(lang, "leave.reason_label")}: {it.reason}</div>}
              </div>
              {canEdit && (
                <button onClick={() => del(it._id)} style={css.btnDanger} data-testid={`leave-del-${it._id}`}>{attT(lang, "delete")}</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, theme, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, color: theme.textMuted }}>{label}</label>
      {children}
    </div>
  );
}

const makeStyles = (theme) => ({
  err: { background: theme.errorBg, color: theme.errorText, padding: 10, borderRadius: 8, marginBottom: 10 },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 },
  count: { fontSize: 14, color: theme.textMuted },
  btnPrimary: { background: theme.gold, color: "#000", border: "none", padding: "10px 20px", borderRadius: 10, cursor: "pointer", fontWeight: 800 },
  btn: { background: theme.surfaceCard, color: theme.text, border: `1px solid ${theme.border}`, padding: "10px 18px", borderRadius: 10, cursor: "pointer", fontWeight: 700 },
  btnDanger: { background: "transparent", color: theme.errorText, border: `1px solid ${theme.errorBorder}`, padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 12 },
  form: { background: theme.surfaceCard, border: `1px solid ${theme.borderAccent}`, borderRadius: 12, padding: 16, marginBottom: 16 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 12 },
  input: { background: theme.surface, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 14, fontFamily: "inherit" },
  formActions: { display: "flex", gap: 10, justifyContent: "flex-end" },
  empty: { textAlign: "center", padding: 30, color: theme.textMuted, background: theme.surfaceAlt, border: `1px dashed ${theme.border}`, borderRadius: 12 },
  list: { display: "flex", flexDirection: "column", gap: 8 },
  row: { display: "flex", gap: 12, alignItems: "center", padding: 12, background: theme.surfaceCard, border: `1px solid ${theme.border}`, borderRadius: 10 },
  rowName: { fontWeight: 800, fontSize: 14, color: theme.text },
  rowMeta: { fontSize: 13, color: theme.textMuted, marginTop: 4 },
  rowReason: { fontSize: 12, color: theme.textMuted, marginTop: 4, fontStyle: "italic" },
});
